import React, {useEffect} from 'react';
import ReportUtils from "../../../../../utils/report-utils.js";
import { booleanLabel, buildRows, getValidations, translateValue, buildBrandModelsMap } from "../utils/vehicle-passages-search-utils.jsx";
import {useQuery} from "@tanstack/react-query";
import bosService from "../../../../../services/web/bos-service.js";

const CompleteAnalysisContent = ({ payload, brandModels }) => {
    const data = payload?.completeAnalysis;

    const brandModelsMap = buildBrandModelsMap( brandModels?.list || [] );

    const { data: boName } = useQuery({
        queryKey: ['boName'],
        queryFn: () => bosService.getBoName(),
        initialData: 'BO'
    });

    useEffect(() => {
        const resumeValues = [];
        const generalValues = [];
        const collectValues = [];
        const classificationValues = [];
        const renavamValues = [];
        const profileValues = [];
        const analyticsValues = [];
        const othersValues = [];

        data.forEach((element) => {
            const { generalValidate, collectValidate, classificationValidate, renavamValidate, profileValidate, analyticValidate, othersValidate } = getValidations(element);

            resumeValues.push([
                ReportUtils.formatText(element.user_name),
                element.date_search,
                element.search,
                booleanLabel(element.similar_plate),
                booleanLabel(element.correspondent_plate),
                element.date_range,
                booleanLabel(element.auto_search)
            ]);

            if (generalValidate) {
                generalValues.push([
                    element.search,
                    translateValue("plate_pattern", element.plate_pattern),
                    booleanLabel(element.recent_passage),
                    booleanLabel(element.main_plate),
                    booleanLabel(element.wrong_way),
                    booleanLabel(element.clausura),
                    booleanLabel(element.expurgadas),
                    booleanLabel(element.with_image)
                ]);
            }

            if (collectValidate) {
                collectValues.push([
                    element.search,
                    ReportUtils.formatText(element.route)
                ]);
            }

            if (classificationValidate) {
                classificationValues.push([
                    element.search,
                    translateValue("vehicle_type", element.vehicle_type),
                    translateValue("brand_model", element.vehicle_classif, brandModelsMap)
                ]);
            }

            if (renavamValidate) {
                renavamValues.push([
                    element.search,
                    booleanLabel(element.registered_vehicles),
                    translateValue("brand_model", element.brand_model, brandModelsMap),
                    ReportUtils.formatText(element.color),
                    translateValue("vehicle_type", element.renavam_type),
                    element.fabrication_year,
                    element.model_year,
                    element.renavam,
                    element.chassi,
                    translateValue("city", element.city),
                    element.uf,
                    element.owner_cnpj,
                    ReportUtils.formatText(element.owner_name),
                    element.possessor_cnpj,
                    ReportUtils.formatText(element.possessor_name)
                ]);
            }

            if (profileValidate) {
                profileValues.push([
                    element.search,
                    element.total_passages,
                    element.daily_passages,
                    element.internal_stay,
                    element.without_passages,
                    element.external_stay,
                    translateValue("standard_flow", element.standard_flow),
                    element.period_values
                ]);
            }

            if (analyticValidate) {
                analyticsValues.push([
                    element.search,
                    element.capture_direction,
                    element.lane_number,
                    element.camera_vehicle_brand,
                    element.camera_vehicle_model,
                    element.camera_vehicle_colors,
                    element.no_seat_belt ? "Sim" : "Não",
                    element.driver_on_phone ? "Sim" : "Não",
                    element.vehicle_speed,
                    element.motorcyclist_without_helmet ? "Sim" : "Não",
                ]);
            }

            if (othersValidate) {
                othersValues.push([
                    element.search,
                    booleanLabel(element.encounter_related),
                    booleanLabel(element.read_not_match),
                    translateValue("occ_status", element.occ_status),
                    element.occ_classif,
                    element.occ_nature,
                    element.dispatch_reasons,
                    booleanLabel(element.occ_marker),
                    element.only_with_adr ? "Sim" : "Não",
                    element.adr_codes
                ]);
            }
        });

        if (resumeValues.length > 0) {
            ReportUtils.createExportableTable(["Agente", "D/H de pesquisa", "Placa pesquisada", "Caracteres similares", "Placas correspondentes", "Intervalo de tempo", "Auto pesquisa"],
                resumeValues,
                "Resumo"
            );
        }

        if (generalValues.length > 0) {
            ReportUtils.createExportableTable(["Placa pesquisada", "Padrão de placa", "Passagens mais recentes", "Apenas placas principais", "Contra-mão", "Ponto clausura", "Imagens expurgadas", "Com imagens"],
                generalValues,
                "Geral"
            );
        }

        if (collectValues.length > 0) {
            ReportUtils.createExportableTable(["Placa pesquisada", "Seleção de câmeras"],
                collectValues,
                "Pontos de coleta"
            );
        }

        if (classificationValues.length > 0) {
            ReportUtils.createExportableTable(["Placa pesquisada", "Tipo", "Marca/Modelo"],
                classificationValues,
                "Classificação"
            );
        }

        if (renavamValues.length > 0) {
            ReportUtils.createExportableTable(["Placa pesquisada", "Veículos cadastrados", "Marca/Modelo", "Cor", "Tipo", "Ano fabricação", "Ano modelo", "RENAVAM", "Chassi", "Município", "Estado", "CPF/CNPJ proprietário", "Nome proprietário", "CPF/CNPJ possuidor", "Nome possuidor"],
                renavamValues,
                "RENAVAM"
            );
        }

        if (profileValues.length > 0) {
            ReportUtils.createExportableTable(["Placa pesquisada", "Total passagens", "Dias com passagem", "Permanência interna", "Dias sem passagem", "Permanência externa", "Fluxo padrão", "Período predominante"],
                profileValues,
                "Perfil"
            );
        }

        if (analyticsValues.length > 0) {
            ReportUtils.createExportableTable(["Placa pesquisada", "Sentido do tráfego", "Faixa de rolagem", "Marca do veículo", "Modelo do veículo", "Cor do veículo", "Motorista ou passageiro sem cinto de segurança", "Motorista no telefone", "Velocidade", "Motociclista sem capacete"],
                analyticsValues,
                "Analítico"
            );
        }

        if (othersValues.length > 0) {
            ReportUtils.createExportableTable(["Placa pesquisada", "Relacionadas com abordagens", "Divergência leitura", "Status ocorrência", "Classificação ocorrência", "Natureza ocorrência", "Natureza despacho", "Marcador passagem", "Somente passagens com leituras de produtos perigosos", "Risco/ONU"],
                othersValues,
                "Outros"
            );
        }

    }, [data, brandModelsMap]);

    if (!data) return null;

    return (
        <>
            <table id="passage-complete-data" className="table report-table" sheet-name="Passagens (completa)">
                <tbody className="bg-white">

                { data.map((element, index) => {
                    const { generalValidate, collectValidate, classificationValidate, renavamValidate, profileValidate, analyticValidate, othersValidate } = getValidations(element);

                    return (

                        <React.Fragment key={index}>

                            <tr className="!bg-[#f6f6f6] text-left">
                                <th className={'w-[18%]'}>Agente</th>
                                <th className={'w-[15%]'}>D/H de pesquisa</th>
                                <th className={'w-[10%] cursor-pointer'} title={'Legenda:\n + : Somente placas lidas pelo sistema\n - : Somente placas não lidas pelo sistema\n ? : Assume qualquer caractere na posição localizada\n * : Assume qualquer sequência de caracteres na posição localizada'}> Placa pesquisada</th>
                                <th className={'w-[10%]'}>Caracteres similares</th>
                                <th className={'w-[10%]'}>Placas correspondentes</th>
                                <th className={'w-[27%]'}>Intervalo de tempo</th>
                                <th className={'w-[10%]'}>Auto pesquisa</th>
                            </tr>

                            <tr className="table-break-word">
                                <td className={'w-[18%]'}>{ReportUtils.formatText(element.user_name)}</td>
                                <td className={'w-[15%]'}>{element.date_search}</td>
                                <td className={'w-[10%] !font-bold'}>{element.search}</td>
                                <td className={'w-[10%]'}>{booleanLabel(element.similar_plate)}</td>
                                <td className={'w-[10%]'}>{booleanLabel(element.correspondent_plate)}</td>
                                <td className={'w-[27%]'}>{element.date_range}</td>
                                <td className={'w-[10%]'}>{booleanLabel(element.auto_search)}</td>
                            </tr>

                            <tr>
                                <td colSpan={7} className="px-3 py-3 bg-[#fafafa]">
                                    <table className="w-full">

                                        <tbody>

                                        { generalValidate ? (

                                            <>
                                                <tr>
                                                    <td colSpan={2} className="!font-bold !py-4 !border-0">
                                                        Filtro avançado - Geral:
                                                    </td>
                                                </tr>

                                                <tr> {buildRows("Padrão de placa", translateValue("plate_pattern", element.plate_pattern), "w-full")} </tr>
                                                <tr> {buildRows("Passagens mais recentes", booleanLabel(element.recent_passage), "w-full")} </tr>
                                                <tr> {buildRows("Apenas placas principais", booleanLabel(element.main_plate), "w-full")} </tr>
                                                <tr> {buildRows("Somente passagens na contra-mão", booleanLabel(element.wrong_way), "w-full")} </tr>
                                                <tr> {buildRows("Somente passagens em pontos clausura", booleanLabel(element.clausura), "w-full")}</tr>
                                                <tr> {buildRows("Somente passagens com imagens expurgadas", booleanLabel(element.expurgadas), "w-full", 2, true)} </tr>
                                                <tr> {buildRows("Somente passagens com imagens", booleanLabel(element.with_image), "w-full", 2, true)} </tr>
                                            </>
                                        ) : null}

                                        { collectValidate ? (
                                            <>
                                                <tr>
                                                    <td colSpan={2} className="!font-bold !py-4 !border-0">
                                                        Filtro avançado - Ponto de coleta:
                                                    </td>
                                                </tr>

                                                <tr> {buildRows("Seleção de câmeras", element.route, "!w-full")} </tr>
                                            </>
                                        ) : null}

                                        { classificationValidate ? (
                                            <>
                                                <tr>
                                                    <td colSpan={2} className="!font-bold !py-4 !border-0">
                                                        Filtro avançado - Classificação de imagem:
                                                    </td>
                                                </tr>

                                                <tr> {buildRows("Tipo", translateValue("vehicle_type", element.vehicle_type, brandModelsMap), "w-full")} </tr>
                                                <tr> {buildRows("Marca/Modelo", translateValue("brand_model", element.vehicle_classif, brandModelsMap), "w-full")} </tr>
                                            </>
                                        ) : null}

                                        { renavamValidate ? (

                                            <>
                                                <tr>
                                                    <td colSpan={2} className="!font-bold !py-4 !border-0">
                                                        Filtro avançado - RENAVAM:
                                                    </td>
                                                </tr>

                                                <tr> {buildRows("Apenas veículos cadastrados", booleanLabel(element.registered_vehicles), "w-full")} </tr>
                                                <tr> {buildRows("Marca/Modelo", translateValue("brand_model", element.brand_model, brandModelsMap), "w-full")} </tr>
                                                <tr> {buildRows("Cor", ReportUtils.formatText(element.color), "w-full")} </tr>
                                                <tr> {buildRows("Tipo", translateValue("vehicle_type", element.renavam_type), "w-full")}</tr>

                                                <tr>
                                                    {buildRows("Ano de fabricação", element.fabrication_year, "w-1/2", 1)}
                                                    {buildRows("Ano do modelo", element.model_year, "w-1/2", 1)}
                                                </tr>

                                                <tr>
                                                    {buildRows("RENAVAM", element.renavam, "w-1/2", 1)}
                                                    {buildRows("Chassi", element.chassi, "w-1/2", 1)}
                                                </tr>

                                                <tr>
                                                    {buildRows("Município", translateValue("city", element.city), "w-1/2", 1)}
                                                    {buildRows("Estado", element.uf, "w-1/2", 1)}
                                                </tr>

                                                <tr>
                                                    {buildRows("CPF/CNPJ do proprietário", element.owner_cnpj, "w-1/2", 1, true)}
                                                    {buildRows("Nome do proprietário", ReportUtils.formatText(element.owner_name), "w-1/2", 1, true)}
                                                </tr>

                                                <tr>
                                                    {buildRows("CPF/CNPJ do possuidor", element.possessor_cnpj, "w-1/2", 1, true)}
                                                    {buildRows("Nome do possuidor", ReportUtils.formatText(element.possessor_name), "w-1/2", 1, true)}
                                                </tr>
                                            </>
                                        ) : null}

                                        { profileValidate ? (

                                            <>
                                                <tr>
                                                    <td colSpan={2} className="!font-bold !py-4 !border-0">
                                                        Filtro avançado - Perfil:
                                                    </td>
                                                </tr>

                                                <tr> {buildRows("Total de passagens", element.total_passages, "w-full")}</tr>
                                                <tr> {buildRows("Dias com passagem", element.daily_passages, "w-full")} </tr>
                                                <tr> {buildRows("Permanência interna", element.internal_stay, "w-full")}</tr>
                                                <tr> {buildRows("Dias sem passagem", element.without_passages, "w-full")} </tr>
                                                <tr> {buildRows("Permanência externa", element.external_stay, "w-full")} </tr>
                                                <tr> {buildRows("Fluxo padrão", translateValue("standard_flow", element.standard_flow), "w-full")} </tr>
                                                <tr> {buildRows("Período predominante", element.period_values, "w-full")} </tr>
                                            </>
                                        ) : null}

                                        { analyticValidate ? (
                                            <>
                                                <tr>
                                                    <td colSpan={2} className="!font-bold !py-4 !border-0">
                                                        Filtro avançado - Analíticos:
                                                    </td>
                                                </tr>

                                                <tr> {buildRows("Sentido do tráfego", element.capture_direction, "w-full")} </tr>
                                                <tr> {buildRows("Faixa de rolagem", element.lane_number, "w-full")} </tr>
                                                <tr> {buildRows("Marca do veículo", element.camera_vehicle_brand, "w-full")} </tr>
                                                <tr> {buildRows("Modelo do veículo", element.camera_vehicle_model, "w-full")} </tr>
                                                <tr> {buildRows("Cor do veículo", element.camera_vehicle_colors, "w-full")} </tr>
                                                <tr> {buildRows("Motorista ou passageiro sem cinto de segurança", element.no_seat_belt ? "Sim" : "Não", "w-full")} </tr>
                                                <tr> {buildRows("Motorista no telefone", element.driver_on_phone  ? "Sim" :  "Não", "w-full")} </tr>
                                                <tr> {buildRows("Velocidade", element.vehicle_speed, "w-full")} </tr>
                                                <tr> {buildRows("Motociclista sem capacete", element.motorcyclist_without_helmet  ? "Sim" : "Não", "w-full")} </tr>
                                            </>
                                        ) : null}

                                        { othersValidate ? (

                                            <>
                                                <tr>
                                                    <td colSpan={2} className="!font-bold !py-4 !border-0">
                                                        Filtro avançado - Outros:
                                                    </td>
                                                </tr>

                                                <tr> {buildRows("Somente passagens marcadas em ocorrências", booleanLabel(element.occ_marker), "w-full")} </tr>
                                                <tr> {buildRows("Placas relacionadas com abordagens", booleanLabel(element.encounter_related), "w-full")} </tr>
                                                <tr> {buildRows("Divergência entre tipo de placa e leitura", booleanLabel(element.read_not_match), "w-full")} </tr>
                                                <tr> {buildRows("Sentry - Ocorrência - Status", translateValue("occ_status", element.occ_status), "w-full")} </tr>
                                                <tr> {buildRows("Sentry - Ocorrência - Natureza", element.occ_nature, "w-full")} </tr>
                                                <tr> {buildRows("Sentry - Ocorrência - Classificação", translateValue("occ_classif", element.occ_classif), "w-full")} </tr>
                                                <tr> {buildRows(`Despacho - ${boName} - Natureza`, element.dispatch_reasons, "w-full")} </tr>
                                                <tr> {buildRows("Somente passagens com leituras de produtos perigosos", element.only_with_adr  ? "Sim" : "Não", "w-full")} </tr>
                                                <tr> {buildRows("Risco/ONU", element.adr_codes, "w-full")} </tr>
                                            </>
                                        ) : null}

                                        </tbody>
                                    </table>

                                </td>
                            </tr>

                        </React.Fragment>
                    );
                })}

                </tbody>
            </table>
        </>
    );
};

export default CompleteAnalysisContent;